import { useRouter } from "next/router";
import { inboxItemHref } from "@remi/constants";
import { ConversationChannel } from "@remi/enums/conversation/channel";
import { ConversationType } from "@remi/enums/conversation/type";
import { OrganizationType } from "@remi/enums/organization-type";
import { RemiButton, type RemiButtonProps, RemiTooltip, RemiTooltipContent, RemiTooltipTrigger } from "@remi/next-ui";

import { useUserProfile } from "~/contexts/UserProfileContext";
import { useConversationUpsert } from "~/hooks/conversation/useConversationUpsert";
import { useOpenPhoneLine } from "~/hooks/useOpenPhoneLine";
import { useBidsByOrganization, useOrganization } from "~/hooks/useOrganization";
import { usePerson } from "~/hooks/usePerson";
import { cn } from "~/lib/utils";

const SEND_MESSAGE_HINT = "Send a message from your number";
const NO_OPEN_PHONE_LINE_HINT = "You need an OpenPhone number to send a message. Assign one from your profile.";
const NO_CONTACT_NUMBER_HINT = "This contact has no phone number";

const getDisabledReason = ({
  hasOpenPhoneLine,
  hasNumber,
}: {
  hasOpenPhoneLine: boolean;
  hasNumber: boolean;
}): string | undefined => {
  if (!hasOpenPhoneLine) {
    return NO_OPEN_PHONE_LINE_HINT;
  }
  if (!hasNumber) {
    return NO_CONTACT_NUMBER_HINT;
  }
  return undefined;
};

// Synthesized only for the icon-only rendering: on the labelled button it would override the visible text and break label-in-name.
const getAriaLabel = ({
  explicitLabel,
  hasVisibleLabel,
  disabledReason,
}: {
  explicitLabel?: string;
  hasVisibleLabel: boolean;
  disabledReason?: string;
}): string | undefined => {
  if (explicitLabel) {
    return explicitLabel;
  }
  if (hasVisibleLabel) {
    return undefined;
  }
  if (disabledReason) {
    return `Send a message — ${disabledReason}`;
  }
  return "Send a message";
};

type TextButtonProps = RemiButtonProps & {
  personId: string;
  number?: string;
};

export const TextButton = ({ personId, number, ...props }: TextButtonProps) => {
  const { data: person } = usePerson(personId);
  const { data: organization } = useOrganization(person?.organizationId);
  const { data: bids } = useBidsByOrganization(
    organization?.type === OrganizationType.HOMEOWNER ? organization.id : undefined,
  );
  const { user } = useUserProfile();
  const openPhoneLine = useOpenPhoneLine();
  const { mutate: upsertConversation, isPending } = useConversationUpsert();

  const router = useRouter();

  if (!person || !organization) {
    return null;
  }

  const disabledReason = getDisabledReason({ hasOpenPhoneLine: !!openPhoneLine, hasNumber: !!number });
  const hint = isPending ? undefined : (disabledReason ?? SEND_MESSAGE_HINT);

  const handleTextClick = () => {
    if (!openPhoneLine || !number) {
      return;
    }

    upsertConversation(
      {
        type: ConversationType.PHONE,
        phoneNumbers: [number, openPhoneLine.number],
        bidIds: organization.type === OrganizationType.HOMEOWNER && bids ? bids.map((bid) => bid.id) : undefined,
        personIds: [person.id, user.person.id],
        // Contractor team routing resolves the contractor from organizationIds. Sent only for that case:
        // organizationIds also mints persistent ConversationToOrganization rows.
        organizationIds: organization.type === OrganizationType.CONTRACTOR ? [organization.id] : undefined,
        channel:
          organization.type === OrganizationType.CONTRACTOR
            ? ConversationChannel.PHONE_REMI_CONTRACTOR
            : ConversationChannel.PHONE_REMI_HOMEOWNER,
      },
      {
        onSuccess: async (conversation) => {
          await router.push(inboxItemHref({ view: "my-inbox", type: "conversation", id: conversation.id }));
        },
      },
    );
  };

  return (
    <RemiTooltip>
      <RemiTooltipTrigger asChild>
        {/* disabled:pointer-events-none on RemiButton lets hover/focus events reach this <span> trigger */}
        <span
          tabIndex={disabledReason || isPending ? 0 : -1}
          className={cn("inline-flex", props.fullWidth && "w-full")}
        >
          <RemiButton
            {...props}
            loading={isPending}
            disabled={disabledReason !== undefined || props.disabled}
            onClick={handleTextClick}
            aria-label={getAriaLabel({
              explicitLabel: props["aria-label"],
              hasVisibleLabel: !!props.children,
              disabledReason,
            })}
          />
        </span>
      </RemiTooltipTrigger>
      {hint && <RemiTooltipContent sideOffset={6}>{hint}</RemiTooltipContent>}
    </RemiTooltip>
  );
};
